import { type VehicleTypeListItem, type VehicleTypeListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { VehicleTypeFormDrawer } from '../components/VehicleTypeFormDrawer';
import {
  useBulkDeleteVehicleTypes,
  useBulkSetVehicleTypesStatus,
  useDeleteVehicleType,
  useVehicleTypesList,
} from '../use-vehicle-types';
import { listVehicleTypes } from '../vehicle-types.api';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const MIN_SEARCH_LENGTH = 2;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const VehicleTypesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteVehicleType = useDeleteVehicleType();
  const bulkDeleteMut = useBulkDeleteVehicleTypes();
  const bulkSetStatusMut = useBulkSetVehicleTypesStatus();

  const columns: ListScreenColumns<VehicleTypeListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<VehicleTypeListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'paxCapacity',
        header: 'Capacity',
        sortable: true,
        width: '120px',
        cell: (row) => (
          <span className="text-zinc-900">
            {row.paxCapacity === null ? '-' : `${row.paxCapacity} pax`}
          </span>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <div className="flex items-center gap-2">
            <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<VehicleTypeListItem, VehicleTypeListQuery>
      title="Vehicle Types"
      description="Transfer vehicles (SEDAN / SUV / INNOVA / TEMPO_TRAVELLER / BUS_25). Capacity helps the editor warn when pax exceeds the chosen vehicle."
      useList={useVehicleTypesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add vehicle type' }}
      renderDrawer={({ open, mode, row, close }) => (
        <VehicleTypeFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          vehicleType={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteVehicleType.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this vehicle type?',
            description: `${row.name} will be soft-deleted and hidden from lists.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} vehicle type${rows.length === 1 ? '' : 's'}?`,
            description: 'These vehicle types will be soft-deleted.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} vehicle type${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in transfer pickers.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listVehicleTypes(q),
        filename: 'vehicle-types',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Pax capacity', value: (r) => r.paxCapacity ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by code or name',
        emptyTitle: 'No vehicle types match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load vehicle types',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
